// Parallel HubSpot submission for the "Financing That Fits"
// leasing/financing inquiry drawer on the product page (id:
// leasing_contact_form, snippets/additional-payment-options.liquid,
// rendered by sections/main-product.liquid). A different form from
// #ProductContactForm on the same page (see product-contact-form-hubspot.js)
// -- this one is the financing offcanvas, not the general "Contact Us
// About This Product" tab.
//
// Grouped with the Leasing forms (assets/leasing-full-form.js /
// leasing-pre-approval-form.js), not with the "Website Lead Forms"
// integrations (Contact Us, Product Contact, Email for Price, Locations,
// Knife Sharpening, Parts & Service) -- per the requester, this drawer is
// the same leasing/financing funnel as those two, just a lighter entry
// point ("Contact Sales" vs. the full wizard). Its own dedicated HubSpot
// form (fields are much lighter than either leasing wizard), but its
// custom properties belong in the "Leasing Application" property group.
// Does NOT send `inquiry_source` -- that's a Website Lead Forms concept;
// neither leasing form uses it either, each just has its own GUID.
//
// NOT reusing Pre-Approval's product_referral_sku/product_referral_title
// -- the live Leasing setup checklist marks both deprecated: a live
// submission got a HubSpot rejection ("isn't a Contact property")
// because neither was ever actually created, and Pre-Approval's code no
// longer sends them (superseded by a newer `equipment_products`
// multi-product field, a different shape -- qty + title + url, no SKU --
// built for a richer "search and link products" UI this drawer doesn't
// have). This uses two new, clearly-named properties instead:
// financing_referral_sku / financing_referral_title.
//
// NOT wired to the near-identical drawer on the standalone /pages/leasing
// page (snippets/leasing-drawer-contact.liquid, assets/leasing-drawer-contact.js)
// -- same form id and mostly the same fields, but a distinct page/snippet,
// covered separately by assets/leasing-drawer-contact-form-hubspot.js.
//
// Routed through the same-origin form relay (Shopify App Proxy ->
// nella-app-proxy -> Linen Plus Middleware -> HubSpot), not a direct
// client-side call to api.hsforms.com -- confirmed blocked by ad
// blockers/tracking protection on a real visitor's browser. See
// docs/testing/form-relay-strategy.html.
//
// Unlike most other integrations, this one has CUSTOM JS validation
// (assets/leasing-drawer.js) that calls preventDefault() on invalid
// email/phone input, on top of native `required`/`pattern` attributes.
// leasing-drawer.js registers its 'submit' listener inside its own
// DOMContentLoaded callback, and its <script> tag (no `defer`) runs
// synchronously, earlier in the page, than this one. To land a 'submit'
// listener AFTER its listener -- so event.defaultPrevented below reflects
// whether that handler already blocked the submission -- this script also
// runs synchronously (no `defer` on its tag in
// snippets/additional-payment-options.liquid) and also waits for its own
// DOMContentLoaded callback, preserving the same registration order.
(function () {
  const RELAY_URL = '/apps/nella-app-login/forms/product_financing';
  const FORM_TOKEN_URL = '/apps/nella-app-login/forms-token';

  document.addEventListener('DOMContentLoaded', function () {
    const formEl = document.getElementById('leasing_contact_form');
    if (!formEl) return;
    if (formEl.dataset.hubspotWired) return;
    formEl.dataset.hubspotWired = 'true';

    // Same first-space split as every other HubSpot integration in this
    // project -- this form also has one combined "Name" field.
    function splitName(fullName) {
      const trimmed = (fullName || '').trim().replace(/\s+/g, ' ');
      if (!trimmed) return { firstName: '', lastName: '' };
      const spaceIndex = trimmed.indexOf(' ');
      if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
      return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
    }

    function buildHubSpotFields() {
      const fields = [];
      const push = (name, value) => {
        const v = (value ?? '').toString().trim();
        if (v) fields.push({ name, value: v });
      };
      const byId = (id) => (document.getElementById(id)?.value || '').toString().trim();
      // contact[sku] and contact[product_title] have no id in the markup --
      // looked up by name attribute, scoped to this form so the drawer's
      // own instance is used, not any other form on the page.
      const byName = (name) => (formEl.querySelector(`input[name="${name}"]`)?.value || '').toString().trim();
      const { firstName, lastName } = splitName(byId('LeasingContactFormName'));

      push('email', byId('LeasingContactFormEmail'));
      push('firstname', firstName);
      push('lastname', lastName);
      push('phone', byId('LeasingContactFormPhone'));
      push('company', byId('LeasingContactBusinessName'));
      // New properties, "Leasing Application" group -- see the header
      // comment for why these aren't product_referral_sku/title.
      push('financing_referral_sku', byName('contact[sku]'));
      push('financing_referral_title', byName('contact[product_title]'));
      // The selected term (24/36/48/60 months). Optional: the plan
      // radios aren't `required`, so a visitor can reach Contact Sales
      // without picking one. Filed under "Leasing Application", not
      // "Website Lead Forms" -- this concept doesn't exist on either
      // leasing wizard, it's specific to this drawer.
      push('requested_financing_term', byId('selectedFinancingTerm--leasing'));

      return fields;
    }

    // Fetched up front and refreshed periodically, not at submit time: a
    // valid submission here navigates away immediately (leasing-drawer.js
    // only calls preventDefault() on invalid input), which tears down this
    // script's JS context before a token fetched right then could reliably
    // resolve -- `keepalive` only protects an already-sent request, not a
    // `.then()` waiting to send a second one after it. A pre-fetched token
    // keeps submit time down to that one keepalive request.
    const FORM_TOKEN_REFRESH_MS = 5 * 60 * 1000; // server TTL is 10 min; refresh with margin
    let cachedFormToken = null;

    function fetchFormToken() {
      return fetch(FORM_TOKEN_URL)
        .then((r) => r.json())
        .then((data) => (data && data.token) || null)
        .catch(() => null);
    }

    function refreshFormToken() {
      fetchFormToken().then((token) => { cachedFormToken = token; });
    }

    refreshFormToken();
    setInterval(refreshFormToken, FORM_TOKEN_REFRESH_MS);

    function submitToHubSpot() {
      try {
        const payload = {
          fields: buildHubSpotFields(),
          pageUri: window.location.href,
          pageName: document.title,
        };
        fetch(RELAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, formToken: cachedFormToken }),
          keepalive: true,
        }).catch((err) => console.warn('HubSpot relay failed (Shopify submission is unaffected):', err));
      } catch (err) {
        console.warn('HubSpot relay failed (Shopify submission is unaffected):', err);
      }
    }

    formEl.addEventListener('submit', (event) => {
      // leasing-drawer.js's own submit handler runs first (registration
      // order) and calls preventDefault() when its email/phone validation
      // fails -- skip firing HubSpot for a submission that never actually
      // reaches Shopify.
      if (event.defaultPrevented) return;
      submitToHubSpot();
    });
  });
})();
